/**
 * Glamora - product page
 *
 * <product-info> (sections/main-product.liquid):
 *  - variant picking: hidden id input, ?variant= URL, price HTML, add-to-cart state,
 *    selected value labels and availability styling of the other option values
 *  - media gallery: one big image in a scroll-snap track (native swipe), round prev/next
 *    arrows (wrap around), thumbnail row, arrow keys / Home / End, jumps to the selected
 *    variant's featured media
 *  - quantity stepper, size guide drawer (Glamora.openDrawer), smart sticky info column
 *
 * Works for products with a single (default) variant: the picker is simply absent.
 */
(() => {
  if (!('customElements' in window) || window.customElements.get('product-info')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const GALLERY_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

  const matches = (query) => window.matchMedia && window.matchMedia(query).matches;
  const isDesktop = () => matches(DESKTOP_QUERY);
  const scrollBehavior = (smooth) =>
    smooth && !matches('(prefers-reduced-motion: reduce)') ? 'smooth' : 'auto';

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const formatMoney = (cents) => {
    const glamora = window.Glamora;
    if (glamora && typeof glamora.formatMoney === 'function') return glamora.formatMoney(cents);
    return (Number(cents || 0) / 100).toFixed(2);
  };

  const matchesOptions = (variant, selected) =>
    selected.every(
      (value, index) => value == null || (Array.isArray(variant.options) && variant.options[index] === value)
    );

  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleGalleryScroll = this.handleGalleryScroll.bind(this);
      this.handleGalleryKeydown = this.handleGalleryKeydown.bind(this);
      this.handleGalleryPointer = this.handleGalleryPointer.bind(this);
      this.updateSticky = this.updateSticky.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
    }

    connectedCallback() {
      this.variants = this.readJson('[data-product-json]') || [];
      const optionNames = this.readJson('[data-product-options]') || [];
      this.optionFieldsets = Array.from(this.querySelectorAll('[data-option-index]'));
      this.optionCount = Math.max(optionNames.length, this.optionFieldsets.length);

      this.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);
      window.addEventListener('pageshow', this.handlePageShow);

      this.initGallery();
      this.initSticky();
      this.initModels();
      this.checkSwatchColors();
      if (this.optionFieldsets.length) this.updateAvailability(this.getSelectedOptions());
      this.syncQtyButtons();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('pageshow', this.handlePageShow);
      window.removeEventListener('resize', this.updateSticky);
      if (this.track) {
        this.track.removeEventListener('scroll', this.handleGalleryScroll);
        this.track.removeEventListener('pointerdown', this.handleGalleryPointer);
      }
      if (this.gallery) this.gallery.removeEventListener('keydown', this.handleGalleryKeydown);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.pendingTimer);
      this.scrollFrame = null;
    }

    readJson(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (error) {
        return null;
      }
    }

    /* ------------------------------------------------------------------ events */

    handleChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches('[data-option-input]')) {
        this.onVariantChange();
      } else if (target.matches('[data-qty-input]')) {
        this.clampQty(target);
        this.syncQtyButtons();
      }
    }

    handleClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const qtyButton = target.closest('[data-qty-btn]');
      if (qtyButton) {
        this.stepQty(qtyButton);
        return;
      }
      if (target.closest('[data-gallery-prev]')) {
        this.goToSlide(this.activeIndex - 1, true);
        return;
      }
      if (target.closest('[data-gallery-next]')) {
        this.goToSlide(this.activeIndex + 1, true);
        return;
      }
      const thumb = target.closest('[data-gallery-thumb]');
      if (thumb) {
        this.goToSlide(Number(thumb.dataset.index), true);
        return;
      }
      const openGuide = target.closest('[data-size-guide-open]');
      if (openGuide) {
        event.preventDefault();
        this.openSizeGuide(openGuide);
        return;
      }
      const closeGuide = target.closest('[data-size-guide-close]');
      if (closeGuide) {
        event.preventDefault();
        this.closeSizeGuide();
      }
    }

    handlePageShow(event) {
      // Back/forward cache can restore checked radios that no longer match the hidden id.
      if (event.persisted && this.optionFieldsets.length) this.onVariantChange({ silent: true });
    }

    /* ---------------------------------------------------------------- variants */

    getSelectedOptions() {
      const selected = new Array(this.optionCount).fill(null);
      this.optionFieldsets.forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        const checked = fieldset.querySelector('[data-option-input]:checked');
        if (checked && index < selected.length) selected[index] = checked.value;
      });
      return selected;
    }

    findVariant(selected) {
      return (
        this.variants.find((variant) => variant.available && matchesOptions(variant, selected)) ||
        this.variants.find((variant) => matchesOptions(variant, selected)) ||
        null
      );
    }

    onVariantChange(options = {}) {
      const selected = this.getSelectedOptions();
      const variant = this.findVariant(selected);

      this.updateSelectedLabels(selected);
      this.updateAvailability(selected);
      this.updateButtons(variant);
      this.clearError();

      if (variant) {
        this.setVariantId(variant.id);
        this.updatePrice(variant);
        this.updateQuantityRules(variant);
        this.updateUrl(variant);
        if (!options.silent && variant.featured_media) this.goToMedia(variant.featured_media.id, true);
      }

      this.dispatchEvent(
        new CustomEvent('glamora:variant-change', {
          bubbles: true,
          detail: { variant, sectionId: this.dataset.sectionId },
        })
      );
    }

    updateSelectedLabels(selected) {
      this.optionFieldsets.forEach((fieldset) => {
        const value = selected[Number(fieldset.dataset.optionIndex)];
        fieldset.querySelectorAll('[data-selected-value]').forEach((label) => {
          label.textContent = value == null ? '' : value;
        });
      });
    }

    updateAvailability(selected) {
      this.optionFieldsets.forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('[data-option-input]').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const available = this.variants.some(
            (variant) => variant.available && matchesOptions(variant, candidate)
          );
          input.classList.toggle('is-unavailable', !available);
          const label = input.labels && input.labels[0];
          const note = label && label.querySelector('[data-unavailable-text]');
          if (note) note.hidden = available;
        });
      });
    }

    updateButtons(variant) {
      let text = this.dataset.textAdd;
      let disabled = false;
      if (!variant) {
        text = this.dataset.textUnavailable;
        disabled = true;
      } else if (!variant.available) {
        text = this.dataset.textSoldOut;
        disabled = true;
      }

      this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        button.disabled = disabled;
        const label = button.querySelector('[data-add-to-cart-text]') || button;
        if (text) label.textContent = text;
      });
      this.querySelectorAll('[data-payment-button]').forEach((wrapper) => {
        wrapper.hidden = disabled;
      });
    }

    clearError() {
      this.querySelectorAll('[data-form-error]').forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
    }

    setVariantId(id) {
      this.querySelectorAll('[data-variant-id]').forEach((input) => {
        if (String(input.value) === String(id)) return;
        input.value = id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /** Same markup as snippets/price.liquid (variant mode). */
    priceHtml(variant) {
      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      let html = onSale
        ? `<span class="visually-hidden">${escapeHtml(this.dataset.textSalePrice)}</span>` +
          `<span class="price__sale">${formatMoney(variant.price)}</span>` +
          `<span class="visually-hidden">${escapeHtml(this.dataset.textRegularPrice)}</span>` +
          `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
        : `<span class="price__regular">${formatMoney(variant.price)}</span>`;

      const measurement = variant.unit_price_measurement;
      if (measurement && variant.unit_price != null) {
        const reference = Number(measurement.reference_value) !== 1 ? escapeHtml(measurement.reference_value) : '';
        html +=
          `<span class="price__unit"><span class="visually-hidden">${escapeHtml(this.dataset.textUnitPrice)}</span>` +
          `${formatMoney(variant.unit_price)}<span aria-hidden="true">/</span>${reference}${escapeHtml(
            measurement.reference_unit
          )}</span>`;
      }
      return { html, onSale };
    }

    updatePrice(variant) {
      const { html, onSale } = this.priceHtml(variant);

      this.querySelectorAll('[data-price]').forEach((price) => {
        price.innerHTML = html;
        price.classList.toggle('price--on-sale', onSale);
        price.classList.toggle('price--sold-out', !variant.available);
      });

      this.querySelectorAll('[data-sale-badge]').forEach((badge) => {
        badge.hidden = !onSale;
      });
    }

    updateQuantityRules(variant) {
      const input = this.querySelector('[data-qty-input]');
      const rule = variant.quantity_rule;
      if (!input || !rule) return;
      input.min = rule.min || 1;
      input.step = rule.increment || 1;
      if (rule.max) input.max = rule.max;
      else input.removeAttribute('max');
      this.clampQty(input);
      this.syncQtyButtons();
    }

    updateUrl(variant) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* ignore (e.g. sandboxed preview) */
      }
    }

    checkSwatchColors() {
      if (!window.CSS || typeof window.CSS.supports !== 'function') return;
      this.querySelectorAll('.product-swatch').forEach((swatch) => {
        const value = swatch.style.getPropertyValue('--swatch').trim();
        if (value && !window.CSS.supports('color', value)) swatch.style.removeProperty('--swatch');
      });
    }

    /* ----------------------------------------------------------------- gallery */

    initGallery() {
      this.gallery = this.querySelector('[data-gallery]');
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.gallery || !this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.gallery.querySelectorAll('[data-gallery-thumb]'));
      this.thumbStrip = this.gallery.querySelector('[data-gallery-thumbs]');
      this.pendingIndex = null;

      const initial = Math.max(0, Number(this.gallery.dataset.initialIndex) || 0);
      this.activeIndex = Math.min(initial, Math.max(0, this.slides.length - 1));

      this.track.addEventListener('scroll', this.handleGalleryScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.handleGalleryPointer, { passive: true });
      this.gallery.addEventListener('keydown', this.handleGalleryKeydown);

      // Open on the selected variant's image without animating.
      if (this.activeIndex > 0) this.goToSlide(this.activeIndex, false, true);
    }

    handleGalleryPointer() {
      // The shopper takes over (swipe/drag): follow the scroll position again.
      this.clearPending();
    }

    handleGalleryScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncActiveSlide();
      });
    }

    clearPending() {
      this.pendingIndex = null;
      clearTimeout(this.pendingTimer);
    }

    /** Index of the slide whose left edge is closest to the track's left edge. */
    nearestSlideIndex() {
      const trackLeft = this.track.getBoundingClientRect().left;
      let best = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    syncActiveSlide() {
      if (!this.slides || !this.slides.length) return;
      const index = this.nearestSlideIndex();
      if (this.pendingIndex != null) {
        // A programmatic (smooth) scroll is running: ignore the slides it passes on the way.
        if (index !== this.pendingIndex) return;
        this.clearPending();
      }
      this.setActiveSlide(index);
    }

    setActiveSlide(index, force) {
      if (index === this.activeIndex && !force) return;
      this.activeIndex = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.revealThumb(index);
      this.pauseInactiveMedia(index);
    }

    /** Scrolls only the thumbnail strip (never the page) so the active thumb is visible. */
    revealThumb(index) {
      const strip = this.thumbStrip;
      const thumb = this.thumbs[index];
      if (!strip || !thumb || strip.scrollWidth <= strip.clientWidth + 1) return;
      const stripRect = strip.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      if (rect.left >= stripRect.left && rect.right <= stripRect.right) return;
      const left = strip.scrollLeft + (rect.left - stripRect.left) - (stripRect.width - rect.width) / 2;
      strip.scrollTo({ left, behavior: scrollBehavior(true) });
    }

    goToSlide(index, smooth, force) {
      if (!this.slides || !this.slides.length || !this.track) return;
      const count = this.slides.length;
      const target = ((Math.round(index) % count) + count) % count; // wrap around
      const slide = this.slides[target];
      const delta = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;

      if (Math.abs(delta) > 1) {
        this.pendingIndex = target;
        clearTimeout(this.pendingTimer);
        this.pendingTimer = setTimeout(() => this.clearPending(), 1000);
        this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: scrollBehavior(smooth) });
      }
      this.setActiveSlide(target, force);
    }

    goToMedia(mediaId, smooth) {
      if (!this.slides) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goToSlide(index, smooth);
    }

    handleGalleryKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!GALLERY_KEYS.includes(event.key) || !this.slides || this.slides.length < 2) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('video, iframe, model-viewer, input, select, textarea')) return;

      const thumb = target.closest('[data-gallery-thumb]');
      const onTrack = target === this.track;
      if (!thumb && !onTrack && !target.closest('[data-gallery-prev], [data-gallery-next]')) return;
      if ((event.key === 'Home' || event.key === 'End') && !thumb && !onTrack) return;

      const rtl = window.getComputedStyle(this.track).direction === 'rtl';
      let index = this.activeIndex;
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.slides.length - 1;
      else {
        const forward = (event.key === 'ArrowRight') !== rtl;
        index += forward ? 1 : -1;
      }

      event.preventDefault();
      this.goToSlide(index, true);
      if (thumb) {
        const next = this.thumbs[this.activeIndex];
        if (next) next.focus({ preventScroll: true });
      }
    }

    pauseInactiveMedia(activeIndex) {
      this.slides.forEach((slide, index) => {
        if (index === activeIndex) return;
        slide.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
        slide.querySelectorAll('iframe').forEach((frame) => {
          try {
            if (/youtube/i.test(frame.src)) {
              frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            } else if (/vimeo/i.test(frame.src)) {
              frame.contentWindow.postMessage('{"method":"pause"}', '*');
            }
          } catch (error) {
            /* cross-origin frame not ready */
          }
        });
      });
    }

    initModels() {
      if (!this.querySelector('model-viewer')) return;
      const shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;
      shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (errors || !window.Shopify || typeof window.Shopify.ModelViewerUI !== 'function') return;
            this.querySelectorAll('model-viewer').forEach((viewer) => {
              if (viewer.dataset.uiReady) return;
              viewer.dataset.uiReady = 'true';
              try {
                this.modelViewers = this.modelViewers || [];
                this.modelViewers.push(new window.Shopify.ModelViewerUI(viewer));
              } catch (error) {
                /* model viewer unavailable: preview image stays visible */
              }
            });
          },
        },
      ]);
    }

    /* -------------------------------------------------------------- sticky info */

    initSticky() {
      this.stickyInfo = this.querySelector('[data-sticky-info]');
      if (!this.stickyInfo) return;
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateSticky);
        this.resizeObserver.observe(this.stickyInfo);
      }
      window.addEventListener('resize', this.updateSticky, { passive: true });
      this.updateSticky();
    }

    updateSticky() {
      const info = this.stickyInfo;
      if (!info) return;
      if (!isDesktop()) {
        info.style.removeProperty('--product-sticky-top');
        return;
      }
      const header = parseFloat(getComputedStyle(this).getPropertyValue('--header-height')) || 72;
      const gap = 24;
      const viewport = window.innerHeight;
      const height = info.offsetHeight;
      // Short column: stick under the header. Tall column: stick by its bottom edge so all of it stays reachable.
      const top = height + header + gap * 2 <= viewport ? header + gap : viewport - height - gap;
      info.style.setProperty('--product-sticky-top', `${Math.round(top)}px`);
    }

    /* ----------------------------------------------------------------- quantity */

    stepQty(button) {
      const wrapper = button.closest('.qty') || this;
      const input = wrapper.querySelector('[data-qty-input]');
      if (!input) return;
      const step = Number(input.step) || 1;
      const current = Number(input.value) || 0;
      input.value = current + (button.dataset.qtyBtn === 'minus' ? -step : step);
      this.clampQty(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clampQty(input) {
      const min = Number(input.min) || 1;
      const step = Number(input.step) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      let value = Math.round(Number(input.value));
      if (!Number.isFinite(value) || value < min) value = min;
      if (step > 1) value = Math.max(min, Math.round(value / step) * step);
      if (value > max) value = Math.max(min, Math.floor(max / step) * step);
      input.value = value;
    }

    syncQtyButtons() {
      const input = this.querySelector('[data-qty-input]');
      if (!input) return;
      const value = Number(input.value);
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const minus = this.querySelector('[data-qty-btn="minus"]');
      const plus = this.querySelector('[data-qty-btn="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }

    /* --------------------------------------------------------------- size guide */

    // Glamora.openDrawer/closeDrawer handle the overlay, body lock, Escape, focus return and
    // aria-expanded on every [aria-controls="<drawer id>"]; the fallback keeps it usable without global.js.
    openSizeGuide(trigger) {
      const id = trigger.getAttribute('aria-controls');
      const drawer = id ? document.getElementById(id) : null;
      if (!drawer) return;
      this.sizeGuideTrigger = trigger;
      const glamora = window.Glamora;
      if (glamora && typeof glamora.openDrawer === 'function') {
        glamora.openDrawer(drawer, trigger);
        return;
      }
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      drawer.focus();
    }

    closeSizeGuide() {
      const drawer = this.querySelector('[data-size-guide]');
      if (!drawer) return;
      const glamora = window.Glamora;
      if (glamora && typeof glamora.closeDrawer === 'function') {
        glamora.closeDrawer(drawer);
        return;
      }
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (this.sizeGuideTrigger) {
        this.sizeGuideTrigger.setAttribute('aria-expanded', 'false');
        this.sizeGuideTrigger.focus({ preventScroll: true });
      }
    }
  }

  window.customElements.define('product-info', ProductInfo);
})();
